import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import useGetMutationFn from '../use-get-mutation-fn';
import { assetKeys } from './assets.query';

export type QueueReason =
  | 'nudity' | 'violence' | 'child_safety' | 'flagged' | 'contact_info' | 'duplicate' | 'watermark' | 'stock';

export type ModerationAction = 'approve' | 'reject' | 'hide' | 'restore' | 'flag' | 'unflag';

export type AssetModerationState = {
  status: 'approved' | 'rejected' | 'hidden' | null;
  reason_code: string | null;
  note: string | null;
  decided_at: string | null;
  flagged: number;
  flag_note: string | null;
} | null;

export type QueueItem = Asset & {
  listing_name: string | null;
  why: { reason: QueueReason; signal_key?: string; label?: string; value?: number | null; reason_code?: string | null; note?: string | null }[];
  suggested_reason: string | null;
  description: string | null;
  moderation: AssetModerationState;
};

export type ReviewQueue = {
  page: number;
  page_size: number;
  total: number;
  reason: QueueReason | null;
  counts: Record<QueueReason, number>;
  /** Distinct photos across every reason. */
  total_all: number;
  items: QueueItem[];
  /** Seller-facing reject/hide reasons: code -> what the seller reads. */
  reasons: Record<string, string>;
  order: QueueReason[];
};

export type ModerateResult = {
  action: ModerationAction;
  ok: number;
  failed: number;
  results: { asid: number; ok: boolean; error?: string; promoted_cover_asid?: number | null }[];
  notices: { eid: number; action: string; asids: number[]; sent: boolean }[];
};

export const QUEUE_LABELS: Record<QueueReason, string> = {
  nudity: 'Nudity',
  violence: 'Violence',
  child_safety: 'Child safety',
  flagged: 'Flagged by staff',
  contact_info: 'Contact details',
  duplicate: 'Duplicates',
  watermark: 'Watermarks',
  stock: 'Stock photos',
};

const unwrap = (raw: any) => (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw ?? {});

export const useReviewQueueQuery = (reason: QueueReason | '', page: number, pageSize: number) => {
  const request = useClientStore((s) => s.request);
  const get = withErrorHandling(async (): Promise<ReviewQueue> => {
    const qs = new URLSearchParams({ page: String(page), page_size: String(pageSize), ...(reason ? { reason } : {}) });
    return unwrap(await request(`/api/admin/assets/review?${qs}`));
  }, 'Could not load the review queue');
  return useQuery({ queryKey: ['asset-review', reason, page, pageSize], queryFn: get, placeholderData: keepPreviousData });
};

export type ModeratePayload = { asids: number[]; action: ModerationAction; reason_code?: string | null; note?: string | null };

export const useModerateAssetsMutation = () => {
  const qc = useQueryClient();
  const fn = useGetMutationFn<ModeratePayload, any>({
    defaultErrorMessage: 'Could not apply the decision',
    endpoint: '/api/admin/assets/moderate',
    method: 'POST',
  });
  return useMutation({
    mutationFn: async (p: ModeratePayload): Promise<ModerateResult> => unwrap(await fn(p)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['asset-review'] });
      qc.invalidateQueries({ queryKey: assetKeys.all });
      qc.invalidateQueries({ queryKey: ['asset-signals'] });
    },
  });
};

/** The same reasons GET /review returns, for screens that have not loaded it. */
export const REJECT_REASONS: Record<string, string> = {
  nudity_sexual: 'Nudity or sexual content',
  violence: 'Violence, gore or weapons',
  child_safety: 'Not appropriate where children are shown',
  contact_info: 'Phone numbers, emails or links in the photo',
  watermark: "Watermark or another seller's branding",
  not_real: 'Stock or catalogue photo, not the actual item',
  wrong_item: 'Does not show the item being listed',
  duplicate_or_stolen: "Copied from another seller's listing",
  low_quality: 'Too blurry, dark or small to be useful',
  other: 'Other (see note)',
};
